import { IUseTreatmentResult, useTreatment } from '@splitsoftware/splitio-react'

import { useSeller } from '@/contexts/seller'

export const SPLIT_EXPERIMENT_VARIANTS = {
  CONTROL: 'control',
  VARIANT: 'variant',
} as const

export type SplitExperimentVariant =
  (typeof SPLIT_EXPERIMENT_VARIANTS)[keyof typeof SPLIT_EXPERIMENT_VARIANTS]

export const DEFAULT_SPLIT_EXPERIMENT_VARIANT_TREATMENTS = [
  'on',
  'variant',
] as const

export const DEFAULT_SPLIT_EXPERIMENT_CONTROL_TREATMENTS = ['off'] as const

type SplitExperimentTreatmentOptions = {
  variantTreatments?: readonly string[]
  controlTreatments?: readonly string[]
}

type UseSplitExperimentProps = SplitExperimentTreatmentOptions & {
  splitName: string
  attributes?: Record<string, unknown>
}

const getTreatmentOptions = ({
  variantTreatments = DEFAULT_SPLIT_EXPERIMENT_VARIANT_TREATMENTS,
  controlTreatments = DEFAULT_SPLIT_EXPERIMENT_CONTROL_TREATMENTS,
}: SplitExperimentTreatmentOptions = {}) => ({
  variantTreatments,
  controlTreatments,
})

export const getSplitExperimentVariant = (
  treatment?: string,
  options?: SplitExperimentTreatmentOptions
): SplitExperimentVariant => {
  const { variantTreatments } = getTreatmentOptions(options)

  return treatment && variantTreatments.includes(treatment)
    ? SPLIT_EXPERIMENT_VARIANTS.VARIANT
    : SPLIT_EXPERIMENT_VARIANTS.CONTROL
}

export const isSplitExperimentTreatmentReady = (
  treatment?: string,
  options?: SplitExperimentTreatmentOptions
) => {
  const { variantTreatments, controlTreatments } = getTreatmentOptions(options)

  return Boolean(
    treatment &&
    (variantTreatments.includes(treatment) ||
      controlTreatments.includes(treatment))
  )
}

const useSplitExperiment = ({
  splitName,
  attributes = {},
  variantTreatments = DEFAULT_SPLIT_EXPERIMENT_VARIANT_TREATMENTS,
  controlTreatments = DEFAULT_SPLIT_EXPERIMENT_CONTROL_TREATMENTS,
}: UseSplitExperimentProps) => {
  const { sellerData } = useSeller()

  const pocId = sellerData?.seller?.id
  const province = sellerData?.seller?.address?.provinceCode ?? ''
  const city = sellerData?.seller?.address?.city ?? ''

  const { treatment = '', isReady: isSplitReady = false }: IUseTreatmentResult =
    useTreatment({
      name: splitName,
      attributes: { ...attributes, poc_id: pocId ?? -1, province, city },
      splitKey: pocId ? String(pocId) : process.env.SPLIT_IO_KEY,
    })

  const treatmentOptions = { variantTreatments, controlTreatments }
  const isReady =
    Boolean(pocId) &&
    isSplitReady &&
    isSplitExperimentTreatmentReady(treatment, treatmentOptions)
  const variantName = getSplitExperimentVariant(
    isReady ? treatment : undefined,
    treatmentOptions
  )

  return {
    splitName,
    treatment,
    variantName,
    variantId: variantName,
    isReady,
    isVariant: variantName === SPLIT_EXPERIMENT_VARIANTS.VARIANT,
  }
}

export default useSplitExperiment
